// HTML 模式渲染管线（方案 A：iframe srcdoc 隔离）
//
// 上传的 HTML 是一个完整文档，CSS/JS 完全自控。若直接 innerHTML 注入主文档：
//   - 用户的 `* { margin:0 }` / `body { display:flex }` 这类全局选择器会污染
//     外层编辑页 UI（实测把工具栏按钮文字挤成竖排单字）
//   - 用户的 <script> 默认不执行，需要 cloneAndReplace 等 hack
//
// 解法：把整页 HTML 塞进 sandbox 化的 <iframe srcdoc>：
//   - srcdoc：浏览器把字符串当作完整文档解析（含 head/body/style/script）
//   - sandbox：放行脚本/弹窗/表单/弹框/下载/用户手势顶层导航（具体见 SANDBOX
//     常量），让外链跳转、表单提交、alert、下载等正常工作；但【刻意不加
//     allow-same-origin】→ iframe 保持 opaque origin，读不到 parent 的
//     cookie/secret/DOM/localStorage，parent 也读不到 iframe 内部。
//   - referrerpolicy="no-referrer"：iframe 内任何 fetch / img 不带主站 referer
//
// 安全模型：
//   - 上传受密钥保护，站主信任自己上传的 HTML 内嵌 JS
//   - sandbox 兜底：即便用户 HTML 被替换为恶意内容，也只能在 iframe 内跑
//
// 复用策略：
//   - 容器里已经有 iframe[data-html-resume] → 只改 srcdoc（浏览器仍重载文档，
//     但 DOM 节点保持，编辑器实时预览 layout 更平滑）
//   - 没有 → 新建并 append（首次渲染 / 容器被外部清空）

// sandbox 放行集合 —— 在【不加 allow-same-origin】（保持 opaque origin、隔离
// 主站 localStorage 里的编辑密钥）的前提下，尽量恢复上传 HTML 的常用能力：
//   allow-scripts                           内联/外链 <script> 执行
//   allow-popups (+to-escape-sandbox)       window.open / <a target="_blank"> 外链跳转
//   allow-forms                             <form> 提交
//   allow-modals                            alert / confirm / prompt / print
//   allow-downloads                         下载类链接
//   allow-top-navigation-by-user-activation 用户点击触发的顶层窗口导航
// 仍不可用（opaque origin 固有限制；解开需 allow-same-origin，但那会让上传的
// HTML 读到主站 localStorage 中的编辑密钥，故不放行）：
//   localStorage / sessionStorage / cookie / history.pushState|replaceState
const SANDBOX =
  'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-modals allow-downloads allow-top-navigation-by-user-activation';
const HOST_ATTR = 'data-html-resume';

// srcdoc 文档自身没有 URL，它的 base URL 会回退继承父页面的 URL。于是用户 HTML
// 里的相对锚点 <a href="#sec"> 被解析成 https://站点域名/#sec（指向父页面，而非
// iframe 自身）——点击时浏览器当成"跨文档导航"而非"同文档滚动"，又被 sandbox
// （无 allow-same-origin / allow-top-navigation）拦下，表现为"点目录不滚动"。
// 注入 <base href="about:srcdoc"> 把 base 拉回 iframe 自身：#sec 重新解析为
// about:srcdoc#sec（与文档同 URL 的纯 fragment），锚点跳转 + CSS scroll-behavior
// 即恢复正常。仅在用户未自带 <base> 时注入，尊重用户的显式声明。
const SRCDOC_BASE_TAG = '<base href="about:srcdoc">';

// 内联兜底脚本：opaque origin（无 allow-same-origin）下访问原生 localStorage /
// sessionStorage 会抛 SecurityError，中断依赖它的用户脚本（如"记住暗色模式"）。
// 这里在用户脚本之前、仅当原生不可用时（need 探测）用内存版替换；数据存活于本次
// 页面会话、刷新即清。IIFE 不污染全局，任何失败都 try/catch 吞掉，绝不反过来弄崩
// 用户脚本。/*__resume_storage_shim__*/ 仅作测试识别标记。闭合标签拆写避免提前断开。
const STORAGE_SHIM =
  '<script>/*__resume_storage_shim__*/(function(){' +
  'function mk(){var m=Object.create(null);var api={' +
  'getItem:function(k){k=String(k);return Object.prototype.hasOwnProperty.call(m,k)?m[k]:null;},' +
  'setItem:function(k,v){m[String(k)]=String(v);},' +
  'removeItem:function(k){delete m[String(k)];},' +
  'clear:function(){var ks=Object.keys(m);for(var i=0;i<ks.length;i++){delete m[ks[i]];}},' +
  'key:function(i){var ks=Object.keys(m);return (i>=0&&i<ks.length)?ks[i]:null;}};' +
  "Object.defineProperty(api,'length',{get:function(){return Object.keys(m).length;}});return api;}" +
  "function need(n){try{window[n].getItem('__p__');return false;}catch(e){return true;}}" +
  "['localStorage','sessionStorage'].forEach(function(n){" +
  'if(need(n)){try{Object.defineProperty(window,n,{value:mk(),configurable:true});}catch(e){}}' +
  '});})();</scr' +
  'ipt>';

// srcdoc 渲染前的兜底注入，统一插到文档最前（<head> 内、用户脚本之前）：
//   - base 标签：把相对锚点拉回同文档 fragment，修"点目录不滚动"（详见 SRCDOC_BASE_TAG）；
//     仅在用户未自带 <base> 时注入，尊重用户的显式声明。
//   - storage shim：兜底 localStorage/sessionStorage 防 SecurityError，始终注入。
function withSrcdocFixups(content: string): string {
  const baseTag = /<base\b/i.test(content) ? '' : SRCDOC_BASE_TAG;
  const prelude = baseTag + STORAGE_SHIM;
  const head = /<head[^>]*>/i.exec(content);
  if (head !== null) {
    const at = head.index + head[0].length;
    return content.slice(0, at) + prelude + content.slice(at);
  }
  const html = /<html[^>]*>/i.exec(content);
  if (html !== null) {
    const at = html.index + html[0].length;
    return content.slice(0, at) + '<head>' + prelude + '</head>' + content.slice(at);
  }
  return prelude + content;
}

export function mountHtmlIframe(container: HTMLElement, content: string): void {
  let iframe = container.querySelector<HTMLIFrameElement>(`iframe[${HOST_ATTR}]`);
  if (iframe === null) {
    container.replaceChildren();
    iframe = document.createElement('iframe');
    iframe.setAttribute(HOST_ATTR, '');
    iframe.setAttribute('sandbox', SANDBOX);
    iframe.setAttribute('referrerpolicy', 'no-referrer');
    iframe.setAttribute('title', '简历内容');
    iframe.style.cssText = 'width:100%;height:100%;border:0;display:block;background:#fff';
    container.appendChild(iframe);
  }
  iframe.srcdoc = withSrcdocFixups(content);
}

// 给 PDF 导出用：返回一段独立可打开的 HTML 文档字符串。
// 用户 HTML 通常已经是整页（含 <html>/<head>/<body>），直接返回；
// 是 fragment 时包一层最简文档。
export function ensureFullDocument(content: string): string {
  if (/<html[\s>]/i.test(content)) {
    return content;
  }
  return `<!doctype html><html><head><meta charset="utf-8"></head><body>${content}</body></html>`;
}

// 启发式检测：内容是否是一个整页 HTML 文档（带 <!doctype> 或 <html>）。
// 用于编辑器切到 MD tab 时识别"切错了"，给空状态而非让 markdown-it +
// DOMPurify 默默把 <html>/<head>/<style> 剥成空白。
export function looksLikeFullHtml(content: string): boolean {
  return /<!doctype\s+html/i.test(content) || /<html[\s>]/i.test(content);
}
